import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type {
  Question,
  QuestionDepth,
  Section,
} from "@leaply-interview/shared-types";
import { CopyButton } from "../components/CopyButton";
import {
  type AdminLeaplyPlan,
  type AdminReward,
  type AdminStudy,
  type StudyCost,
  leaplyPlans as leaplyPlansApi,
  rewards as rewardsApi,
  studies,
} from "../lib/api";
import { ErrorBanner, formatApiError, StatusPill } from "./Studies";

/** Average minutes per question by depth — matches `_prototype/lib/studies.ts`
 *  DEPTH_MINUTES and the gates.ts estimator on the api side. */
const DEPTH_MINUTES: Record<QuestionDepth, number> = {
  light: 1.5,
  medium: 3,
  deep: 5,
};

/**
 * /studies/:id — full study editor (ported from
 * `_prototype/app/(admin)/studies/[id]/page.tsx`). Three tabs:
 *
 *   Overview   — StatusBlock callout, participant link, 4 KPI tiles
 *   Setup      — Schedule, Gates, Reward, Plan, Prompt, Estimated time,
 *                Interviewer / Conversation / Warmup / Sections (Soon)
 *   Analytics  — stub (charts/cost cards deferred — task #15 polish)
 *
 * Lifecycle transitions (Publish / Pause / Complete / Restart / Restore /
 * Archive) PATCH the study with just `{status}` and update in place. The
 * Save button PATCHes the full form. Edits to a published study would
 * normally route through a "confirm changes" modal; deferred for v1.
 */
type Tab = "overview" | "setup" | "analytics";

const PARTICIPANT_BASE =
  import.meta.env.VITE_PARTICIPANT_URL ?? "http://localhost:3000";

const VOICES = ["marin", "shimmer", "coral", "alloy", "echo", "cedar", "ash"];

/**
 * Curated language list — must align with what `gpt-realtime` can plausibly
 * generate. Order is "English first, then alpha" to match the prototype.
 * Codes are ISO 639-1 (two letters) except `fil` for Filipino.
 */
const LANGUAGES: Array<{ code: string; label: string; flag: string }> = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "ar", label: "Arabic", flag: "🇦🇪" },
  { code: "bg", label: "Bulgarian", flag: "🇧🇬" },
  { code: "zh", label: "Chinese", flag: "🇨🇳" },
  { code: "hr", label: "Croatian", flag: "🇭🇷" },
  { code: "cs", label: "Czech", flag: "🇨🇿" },
  { code: "da", label: "Danish", flag: "🇩🇰" },
  { code: "nl", label: "Dutch", flag: "🇳🇱" },
  { code: "fil", label: "Filipino", flag: "🇵🇭" },
  { code: "fr", label: "French", flag: "🇫🇷" },
  { code: "de", label: "German", flag: "🇩🇪" },
  { code: "el", label: "Greek", flag: "🇬🇷" },
  { code: "hi", label: "Hindi", flag: "🇮🇳" },
  { code: "it", label: "Italian", flag: "🇮🇹" },
  { code: "ja", label: "Japanese", flag: "🇯🇵" },
  { code: "ko", label: "Korean", flag: "🇰🇷" },
  { code: "pl", label: "Polish", flag: "🇵🇱" },
  { code: "pt", label: "Portuguese", flag: "🇵🇹" },
  { code: "ro", label: "Romanian", flag: "🇷🇴" },
  { code: "ru", label: "Russian", flag: "🇷🇺" },
  { code: "es", label: "Spanish", flag: "🇪🇸" },
  { code: "sv", label: "Swedish", flag: "🇸🇪" },
  { code: "tr", label: "Turkish", flag: "🇹🇷" },
  { code: "uk", label: "Ukrainian", flag: "🇺🇦" },
];

export function StudyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [study, setStudy] = useState<AdminStudy | null>(null);
  const [saved, setSaved] = useState<AdminStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [toast, setToast] = useState<{ key: number; message: string } | null>(
    null,
  );
  // Pending impacts for the ConfirmChangesDialog. Set when Save is clicked
  // on a published study with meaningful changes; nulling it dismisses
  // the modal without saving.
  const [pendingImpacts, setPendingImpacts] = useState<ChangeImpact[] | null>(
    null,
  );

  useEffect(() => {
    if (!id) return;
    studies
      .get(id)
      .then(({ study: s }) => {
        setStudy(s);
        setSaved(s);
      })
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  function showToast(message: string) {
    setToast({ key: Date.now(), message });
  }

  const dirty = useMemo(
    () => (study && saved ? JSON.stringify(study) !== JSON.stringify(saved) : false),
    [study, saved],
  );

  /**
   * Save click handler. For draft / paused / completed / archived studies,
   * commits immediately. For published studies, computes the impact diff
   * vs. `saved` first — if any meaningful changes (those that affect future
   * interviews vs. just metadata), pops the ConfirmChangesDialog and lets
   * the user keep editing or proceed. The actual write goes through
   * `commitSave()` either way.
   */
  function requestSave() {
    if (!id || !study || !saved || saving) return;
    if (study.status === "published") {
      const impacts = diffChangeImpacts(saved, study);
      if (impacts.length > 0) {
        setPendingImpacts(impacts);
        return;
      }
    }
    void commitSave();
  }

  async function commitSave() {
    if (!id || !study || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { study: updated } = await studies.update(id, {
        name: study.name,
        slug: study.slug,
        description: study.description,
        planTarget: study.planTarget,
        systemPrompt: study.systemPrompt,
        voice: study.voice,
        studyType: study.studyType,
        interviewerName: study.interviewerName,
        firstMessage: study.firstMessage,
        language: study.language,
        languages: study.languages,
        sections: study.sections,
        maxValidInterviews: study.maxValidInterviews,
        maxDurationMinutes: study.maxDurationMinutes,
        rewardId: study.rewardId,
        leaplyPlan: study.leaplyPlan,
      });
      setStudy(updated);
      setSaved(updated);
      setPendingImpacts(null);
      showToast("Changes saved");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function transitionStatus(next: AdminStudy["status"]) {
    if (!id || !study || transitioning) return;
    setTransitioning(true);
    try {
      const { study: updated } = await studies.update(id, { status: next });
      setStudy((prev) => (prev ? { ...prev, status: updated.status } : prev));
      setSaved((prev) => (prev ? { ...prev, status: updated.status } : prev));
      const copy = TRANSITION_TOAST[next];
      if (copy) showToast(copy);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setTransitioning(false);
    }
  }

  async function archive() {
    if (!id) return;
    if (!window.confirm("Archive this study? It can be restored later.")) return;
    try {
      await studies.archive(id);
      navigate("/studies");
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  async function duplicate() {
    if (!id) return;
    try {
      const { study: copy } = await studies.duplicate(id);
      navigate(`/studies/${copy.id}`);
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  async function downloadTranscripts() {
    if (!id) return;
    try {
      const { filename, markdown } = await studies.transcripts(id);
      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("Transcripts downloaded");
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  if (loading) {
    return (
      <div className="px-8 py-10 lg:px-14 lg:py-12 text-[13px] text-neutral-500">
        Loading study…
      </div>
    );
  }
  if (error && !study) {
    return (
      <div className="px-8 py-10 lg:px-14 lg:py-12">
        <ErrorBanner message={error} />
      </div>
    );
  }
  if (!study) return null;

  return (
    <div className="min-h-full">
      {toast && (
        <div
          key={toast.key}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-800 shadow-sm"
          role="status"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M3 8.5l3.5 3.5L13 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {toast.message}
        </div>
      )}

      <div className="px-8 py-10 lg:px-14 lg:py-12 max-w-5xl">
        {/* ── Floating top bar — Back + Save ─────────────────────────────── */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <Link
            to="/studies"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wider text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
              <path
                d="M10 3L5 8l5 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </Link>
          <button
            type="button"
            onClick={requestSave}
            disabled={!dirty || saving}
            className={`rounded-md px-4 py-2 text-[13px] font-medium transition-colors ${
              dirty && !saving
                ? "bg-neutral-900 hover:bg-neutral-800 text-white"
                : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
            }`}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        {error && <ErrorBanner message={error} />}

        {/* ── Title + description ───────────────────────────────────────── */}
        <input
          type="text"
          value={study.name}
          onChange={(e) => setStudy({ ...study, name: e.target.value })}
          placeholder="Untitled study"
          className="block w-full font-serif text-[40px] leading-[48px] tracking-tight text-neutral-900 bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-neutral-400 mb-2"
        />
        <textarea
          value={study.description ?? ""}
          onChange={(e) =>
            setStudy({
              ...study,
              description: e.target.value === "" ? null : e.target.value,
            })
          }
          placeholder="Why are you running this study? What do you want to learn?"
          rows={2}
          className="block w-full text-[15px] leading-relaxed text-neutral-700 bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-neutral-400 resize-none mb-5"
        />

        {/* ── ID + status pill + actions ───────────────────────────────── */}
        <div className="flex items-center gap-3 flex-wrap mb-8">
          <span className="inline-flex items-center gap-1.5 font-mono text-[12px] text-neutral-500">
            <span className="text-neutral-400">ID:</span>
            <span className="select-all">{study.id.slice(0, 8)}</span>
            <CopyButton value={study.id} ariaLabel="Copy study ID" />
          </span>
          <span className="text-neutral-300">|</span>
          <StatusPill status={study.status} />
          <span className="text-neutral-300">|</span>
          <button
            type="button"
            onClick={() => void downloadTranscripts()}
            className="text-[12px] text-neutral-500 hover:text-neutral-900 hover:underline inline-flex items-center gap-1"
            title="Download all valid interview transcripts as Markdown"
          >
            <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
              <path
                d="M8 2v8M4 7l4 4 4-4M2 13h12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Download transcripts
          </button>
          <button
            type="button"
            onClick={() => void duplicate()}
            className="text-[12px] text-neutral-500 hover:text-neutral-900 hover:underline"
          >
            Duplicate
          </button>
          {study.status !== "archived" && (
            <button
              type="button"
              onClick={() => void archive()}
              className="text-[12px] text-red-700 hover:underline"
            >
              Archive
            </button>
          )}
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className="border-b border-neutral-200 mb-8">
          <div className="flex items-center gap-6">
            <TabButton label="Overview" active={tab === "overview"} onClick={() => setTab("overview")} />
            <TabButton label="Setup" active={tab === "setup"} onClick={() => setTab("setup")} />
            <TabButton label="Analytics" active={tab === "analytics"} onClick={() => setTab("analytics")} />
          </div>
        </div>

        {/* ── Tab body ───────────────────────────────────────────────────── */}
        {tab === "overview" && (
          <OverviewTab
            study={study}
            transitioning={transitioning}
            onTransition={transitionStatus}
          />
        )}
        {tab === "setup" && (
          <SetupTab study={study} setStudy={setStudy} />
        )}
        {tab === "analytics" && <AnalyticsTab study={study} />}
      </div>

      {/* Published-study save confirmation. Lives outside the inner
          max-w wrapper so the backdrop covers the whole viewport. */}
      {pendingImpacts && (
        <ConfirmChangesDialog
          impacts={pendingImpacts}
          saving={saving}
          onCancel={() => setPendingImpacts(null)}
          onConfirm={() => void commitSave()}
        />
      )}
    </div>
  );
}

// ── ConfirmChangesDialog ──────────────────────────────────────────────────
// Adapted from `_prototype/.../studies/[id]/page.tsx`. Renders when the
// user clicks Save on a `published` study that has impact-bearing diffs.

interface ChangeImpact {
  eyebrow: string;
  body: string;
}

function ConfirmChangesDialog({
  impacts,
  saving,
  onCancel,
  onConfirm,
}: {
  impacts: ChangeImpact[];
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Esc closes — body scroll lock kept simple (no portal needed; the
  // backdrop fills the viewport via fixed inset-0).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onCancel();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onCancel, saving]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-changes-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 relative"
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-40"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M4 4l8 8M12 4l-8 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">
          Confirm changes
        </div>
        <h2
          id="confirm-changes-title"
          className="text-[18px] font-semibold text-neutral-900 mb-1.5"
        >
          Some changes affect future interviews
        </h2>
        <p className="text-[13px] leading-relaxed text-neutral-600 mb-4">
          Past interviews keep their original transcripts and analysis. The
          updates below apply to interviews from this point on; in-flight
          calls keep the version they started with.
        </p>

        <div className="space-y-3 mb-5 max-h-[40vh] overflow-y-auto">
          {impacts.map((imp, i) => (
            <div
              key={i}
              className="rounded-md border border-neutral-200 bg-neutral-50/40 p-3"
            >
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">
                {imp.eyebrow}
              </div>
              <p className="text-[12px] leading-relaxed text-neutral-700">
                {imp.body}
              </p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="text-[13px] font-medium text-neutral-700 hover:text-neutral-900 transition-colors px-3 py-2 disabled:opacity-40"
          >
            Keep editing
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="inline-flex items-center rounded-md bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compute the human-readable impact list for ConfirmChangesDialog. Skipped
 * categories (name, description, slug, planTarget, maxValidInterviews etc.)
 * are pure metadata or capacity numbers that don't change session content —
 * we save those silently. Anything that changes what Lea says, hears, or
 * promises gets surfaced.
 */
function diffChangeImpacts(
  saved: AdminStudy,
  current: AdminStudy,
): ChangeImpact[] {
  const impacts: ChangeImpact[] = [];

  if (
    saved.systemPrompt !== current.systemPrompt ||
    (saved.studyType ?? "discovery") !== (current.studyType ?? "discovery")
  ) {
    impacts.push({
      eyebrow: "Prompt / study type changed",
      body: "Future interviews use the new instructions. Past interviews remain analyzed against the original prompt.",
    });
  }

  if (
    saved.interviewerName !== current.interviewerName ||
    saved.voice !== current.voice ||
    saved.language !== current.language
  ) {
    impacts.push({
      eyebrow: "Interviewer changed",
      body: "Name, voice, or language updates affect future participants only. Past audio keeps the original voice.",
    });
  }

  if ((saved.firstMessage ?? "") !== (current.firstMessage ?? "")) {
    impacts.push({
      eyebrow: "Opening line changed",
      body: "Future calls open with the new line; past transcripts keep what was actually said.",
    });
  }

  if ((saved.rewardId ?? "") !== (current.rewardId ?? "")) {
    impacts.push({
      eyebrow: "Reward changed",
      body: "Past valid interviews keep their originally-promised reward. New valid interviews get the new reward.",
    });
  }

  if ((saved.leaplyPlan ?? "") !== (current.leaplyPlan ?? "")) {
    impacts.push({
      eyebrow: "Leaply plan changed",
      body: "Lea reads the new plan's silent context for future participants. Past interviews stay analyzed against the previous plan.",
    });
  }

  // Gates change which interviews count as valid going forward — surfaces
  // because it can flip the cap-reached auto-complete behaviour.
  const sg = (saved as AdminStudy & { gates?: unknown }).gates;
  const cg = (current as AdminStudy & { gates?: unknown }).gates;
  if (JSON.stringify(sg ?? null) !== JSON.stringify(cg ?? null)) {
    impacts.push({
      eyebrow: "Validity gates changed",
      body: "New thresholds apply to interviews from this point on. Past status / invalid_reason values stay as they were when finalized.",
    });
  }

  return impacts;
}

// ── Toast / transition copy ────────────────────────────────────────────────

const TRANSITION_TOAST: Partial<Record<AdminStudy["status"], string>> = {
  published: "Study published — accepting participants",
  paused: "Study paused — new participants blocked",
  completed: "Study completed — wrapping up",
  draft: "Study moved back to draft",
  archived: "Study archived",
};

// ── Tab button ────────────────────────────────────────────────────────────

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative pb-3 text-[14px] font-medium transition-colors ${
        active ? "text-neutral-900" : "text-neutral-500 hover:text-neutral-700"
      }`}
    >
      {label}
      {active && (
        <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-neutral-900 rounded-full" />
      )}
    </button>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────

function OverviewTab({
  study,
  transitioning,
  onTransition,
}: {
  study: AdminStudy;
  transitioning: boolean;
  onTransition: (next: AdminStudy["status"]) => void;
}) {
  // KPI counts come from the same all-time server aggregate as the My Studies
  // counters and the Analytics cost tab (GET /studies/:id/cost) — not a
  // client-side count of a windowed, paginated list. So a study's numbers
  // match on every page, regardless of interview age or volume.
  const [cost, setCost] = useState<StudyCost | null>(null);

  useEffect(() => {
    let cancelled = false;
    studies
      .cost(study.id)
      .then((c) => {
        if (!cancelled) setCost(c);
      })
      .catch(() => {
        if (!cancelled) setCost(null);
      });
    return () => {
      cancelled = true;
    };
  }, [study.id]);

  const stats = useMemo(() => {
    const count = cost?.interviewCount ?? 0;
    const validCount = cost?.validInterviewCount ?? 0;
    return {
      count,
      validCount,
      avgDurationS: cost?.avgDurationS ?? 0,
      qualityPct: count > 0 ? (validCount / count) * 100 : 0,
    };
  }, [cost]);

  const participantUrl = `${PARTICIPANT_BASE}/interview?studyId=${study.id}`;

  return (
    <div className="space-y-10">
      <StatusBlock
        study={study}
        transitioning={transitioning}
        onTransition={onTransition}
      />

      {/* ── Participant link ────────────────────────────────────────────── */}
      <section>
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2">
          Participant link
        </h2>
        <div className="flex items-center gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2.5">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden className="text-neutral-400 shrink-0">
            <path
              d="M7 5h2a3 3 0 0 1 0 6H7M9 11H7a3 3 0 0 1 0-6h2M6 8h4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <a
            href={participantUrl}
            target="_blank"
            rel="noreferrer"
            className="flex-1 min-w-0 font-mono text-[12px] text-neutral-700 hover:text-neutral-900 truncate"
            title={participantUrl}
          >
            {participantUrl}
          </a>
          <CopyButton value={participantUrl} ariaLabel="Copy participant link" />
        </div>
        <p className="mt-2 text-[13px] text-neutral-500">
          Share this link with participants to start collecting interviews.
        </p>
      </section>

      {/* ── KPI tiles ───────────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          accent="border-t-blue-300"
          label="Participants"
          value={String(stats.count)}
        />
        <KpiCard
          accent="border-t-emerald-300"
          label="Avg completion"
          value={formatMinSec(stats.avgDurationS)}
        />
        <KpiCard
          accent="border-t-amber-300"
          label="Avg quality"
          value={`${Math.round(stats.qualityPct)}%`}
          info="Share of valid interviews"
        />
        <KpiCard
          accent="border-t-violet-300"
          label="Valid"
          value={
            study.maxValidInterviews
              ? `${stats.validCount} / ${study.maxValidInterviews}`
              : String(stats.validCount)
          }
          info={
            study.maxValidInterviews
              ? `Study auto-completes after ${study.maxValidInterviews} valid interviews.`
              : undefined
          }
        />
      </section>
    </div>
  );
}

// ── Status callout block ──────────────────────────────────────────────────

const LIFECYCLE_STYLE: Record<
  AdminStudy["status"],
  { block: string; eyebrow: string; dot: string }
> = {
  draft: {
    block: "bg-neutral-50/60 border-neutral-200",
    eyebrow: "text-neutral-500",
    dot: "bg-neutral-400",
  },
  published: {
    block: "bg-amber-50/40 border-amber-200",
    eyebrow: "text-amber-700",
    dot: "bg-amber-400",
  },
  paused: {
    block: "bg-blue-50/40 border-blue-200",
    eyebrow: "text-blue-700",
    dot: "bg-blue-400",
  },
  completed: {
    block: "bg-emerald-50/40 border-emerald-200",
    eyebrow: "text-emerald-700",
    dot: "bg-emerald-400",
  },
  archived: {
    block: "bg-neutral-50/60 border-neutral-200",
    eyebrow: "text-neutral-500",
    dot: "bg-neutral-400",
  },
};

const LIFECYCLE_COPY: Record<
  AdminStudy["status"],
  {
    eyebrow: string;
    headline: string;
    body: string;
    primary?: { label: string; next: AdminStudy["status"] };
    secondary?: { label: string; next: AdminStudy["status"] }[];
  }
> = {
  draft: {
    eyebrow: "Draft",
    headline: "Not yet live.",
    body:
      "Fill in Setup and publish to start accepting participants. Edits won't affect anyone until you publish.",
    primary: { label: "Publish", next: "published" },
  },
  published: {
    eyebrow: "Running",
    headline: "Your study is live.",
    body:
      "Participants can now start interviews. Pause to halt collection temporarily, or complete the study to wrap up.",
    primary: { label: "Complete", next: "completed" },
    secondary: [{ label: "Pause", next: "paused" }],
  },
  paused: {
    eyebrow: "Paused",
    headline: "Collection paused.",
    body:
      "New participants can't start an interview. In-flight interviews are unaffected. Resume to keep going.",
    primary: { label: "Resume", next: "published" },
    secondary: [{ label: "Complete", next: "completed" }],
  },
  completed: {
    eyebrow: "Completed",
    headline: "Study wrapped up.",
    body:
      "No more participants will join. Restart to re-open collection, or archive to hide from the list.",
    primary: { label: "Restart", next: "published" },
    secondary: [{ label: "Archive", next: "archived" }],
  },
  archived: {
    eyebrow: "Archived",
    headline: "Tucked away.",
    body: "Hidden from My Studies. Restore to bring it back as a draft.",
    primary: { label: "Restore", next: "draft" },
  },
};

function StatusBlock({
  study,
  transitioning,
  onTransition,
}: {
  study: AdminStudy;
  transitioning: boolean;
  onTransition: (next: AdminStudy["status"]) => void;
}) {
  const style = LIFECYCLE_STYLE[study.status];
  const copy = LIFECYCLE_COPY[study.status];
  return (
    <section
      className={`relative rounded-lg border p-5 lg:p-6 ${style.block}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
        <span
          className={`text-[11px] font-semibold uppercase tracking-wider ${style.eyebrow}`}
        >
          {copy.eyebrow}
        </span>
      </div>
      <h3 className="text-[20px] font-semibold text-neutral-900 mb-1">
        {copy.headline}
      </h3>
      <p className="text-[14px] text-neutral-700 leading-relaxed max-w-prose mb-4">
        {copy.body}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {copy.secondary?.map((s) => (
          <button
            key={s.next}
            type="button"
            onClick={() => onTransition(s.next)}
            disabled={transitioning}
            className="rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 px-3 py-1.5 text-[13px] font-medium text-neutral-700 transition-colors disabled:opacity-50"
          >
            {s.label}
          </button>
        ))}
        {copy.primary && (
          <button
            type="button"
            onClick={() => onTransition(copy.primary!.next)}
            disabled={transitioning}
            className="rounded-md bg-neutral-900 hover:bg-neutral-800 px-3 py-1.5 text-[13px] font-medium text-white transition-colors disabled:opacity-50"
          >
            {transitioning ? "Working…" : copy.primary.label}
          </button>
        )}
      </div>
    </section>
  );
}

// ── KPI tile ───────────────────────────────────────────────────────────────

function KpiCard({
  accent,
  label,
  value,
  info,
}: {
  /** Tailwind border-t-* class for the colored top accent. */
  accent: string;
  label: string;
  value: string;
  info?: string;
}) {
  return (
    <div
      className={`rounded-lg border border-neutral-200 border-t-2 ${accent} bg-white px-5 py-4`}
      title={info}
    >
      <div className="text-[28px] font-semibold text-neutral-900 tabular-nums leading-none mb-2">
        {value}
      </div>
      <div className="flex items-center gap-1">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
          {label}
        </div>
        {info && (
          <span
            aria-label={info}
            className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-neutral-100 text-neutral-500 text-[9px] font-bold cursor-help"
          >
            ⓘ
          </span>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatMinSec(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ── Setup tab ──────────────────────────────────────────────────────────────

function SetupTab({
  study,
  setStudy,
}: {
  study: AdminStudy;
  setStudy: (s: AdminStudy) => void;
}) {
  const [showPrompt, setShowPrompt] = useState(false);

  // Plan catalog for the Leaply-plan picker — same source as the /prompts
  // Plan-catalog section (unique plan codes from the courses DB). Null =
  // still loading; [] = load failed (picker degrades to none + legacy).
  const [plans, setPlans] = useState<AdminLeaplyPlan[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    leaplyPlansApi
      .list()
      .then((r) => {
        if (!cancelled) setPlans(r.plans);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Estimated interview time (mirrors apps/api gates.ts logic) ─────────
  const estimate = useMemo(() => {
    const firstWords = (study.firstMessage ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const promptWords = (study.systemPrompt ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const greeting = Math.max(0.5, firstWords / 220);
    const body = Math.max(1, promptWords / 70);
    const minutes = Math.max(1, Math.round(greeting + body + 0.5));
    return { minutes, promptWords };
  }, [study.firstMessage, study.systemPrompt]);

  return (
    <div className="space-y-0">
      <Section label="Study type">
        <StudyTypePicker
          value={study.studyType ?? "discovery"}
          onChange={(v) => setStudy({ ...study, studyType: v })}
        />
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="Schedule">
        <Field
          label="Max valid interviews"
          hint="Enrollment cap. Empty = unlimited. Study auto-completes when hit."
        >
          <input
            type="number"
            min={1}
            value={study.maxValidInterviews ?? ""}
            onChange={(e) =>
              setStudy({
                ...study,
                maxValidInterviews:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Max duration (minutes)"
          hint="Call-length cap, 2–60 (OpenAI hard limit is 60). Lea wraps up naturally 1 minute before; the interview then auto-ends and saves."
        >
          <input
            type="number"
            min={2}
            max={60}
            value={study.maxDurationMinutes ?? 60}
            onChange={(e) =>
              setStudy({
                ...study,
                maxDurationMinutes:
                  e.target.value === "" ? 60 : Number(e.target.value),
              })
            }
            className={INPUT_CLASS}
          />
        </Field>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section
        label="Gates"
        description="Server-side validity rules. When ANY is set, ALL set gates must pass. When none are set, falls back to the legacy ≥ 30 s rule."
      >
        <GatesFields study={study} setStudy={setStudy} />
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="User reward">
        <Field
          label="Reward"
          hint="What valid participants receive on completion. Past valid interviews keep the reward that was promised at finalize-time."
        >
          <RewardPicker
            value={study.rewardId}
            onChange={(next) => setStudy({ ...study, rewardId: next })}
          />
        </Field>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="Leaply plan">
        <Field label="Plan target">
          <select
            value={study.leaplyPlan ?? ""}
            onChange={(e) =>
              setStudy({
                ...study,
                leaplyPlan: e.target.value === "" ? null : e.target.value,
              })
            }
            className={INPUT_CLASS}
          >
            <option value="">— none —</option>
            {/* Legacy value not present in the courses catalog (e.g. old
                'Lymph'/'Kids'/'Vagus' seeds) — keep it selectable so the
                select doesn't silently misrender the saved value. */}
            {study.leaplyPlan &&
              plans !== null &&
              !plans.some((p) => p.id === study.leaplyPlan) && (
                <option value={study.leaplyPlan}>
                  {study.leaplyPlan} (legacy)
                </option>
              )}
            {/* Label = plan CODE (the stable slug stored on the study and
                matched at session-mint), not the display name — names
                duplicate across codes (e.g. two "Vagus nerve reset plan"s)
                and would make the picker ambiguous. */}
            {(plans ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.id}
              </option>
            ))}
          </select>
        </Field>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="Prompt">
        <Field
          label="System prompt"
          hint="Study-specific instructions for Lea. Workspace prompts (Boundaries, Style, Wrap-up) compose around this at session-mint."
        >
          {showPrompt ? (
            <>
              <textarea
                value={study.systemPrompt}
                onChange={(e) =>
                  setStudy({ ...study, systemPrompt: e.target.value })
                }
                rows={18}
                className={`${INPUT_CLASS} font-mono text-[13px] leading-relaxed resize-y`}
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
                <button
                  type="button"
                  onClick={() => setShowPrompt(false)}
                  className="hover:text-neutral-900 hover:underline"
                >
                  Collapse
                </button>
                <span className="tabular-nums">
                  {study.systemPrompt.length} chars · {estimate.promptWords} words
                </span>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setShowPrompt(true)}
              className="w-full rounded-md border border-dashed border-neutral-300 hover:border-neutral-400 hover:bg-neutral-50 px-4 py-6 text-[13px] text-neutral-600 transition-colors"
            >
              {study.systemPrompt
                ? `Show prompt (${estimate.promptWords} words)`
                : "+ Write system prompt"}
            </button>
          )}
        </Field>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="Estimated time">
        <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-white px-4 py-3">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[28px] font-semibold tabular-nums text-neutral-900 leading-none">
              {estimate.minutes}
            </span>
            <span className="text-[13px] text-neutral-500">min</span>
          </div>
          <span className="text-[12px] text-emerald-700 font-medium">
            {(study.studyType ?? "discovery") === "guided"
              ? "guided survey"
              : "discovery"}
            {" · "}
            {estimate.promptWords} word{estimate.promptWords === 1 ? "" : "s"} of prompt
          </span>
        </div>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="Interviewer">
        <Field label="Interviewer name">
          <input
            type="text"
            value={study.interviewerName}
            onChange={(e) =>
              setStudy({ ...study, interviewerName: e.target.value })
            }
            className={INPUT_CLASS}
          />
        </Field>
        <Field label="Voice" hint="One of the OpenAI Realtime voices.">
          <select
            value={study.voice}
            onChange={(e) => setStudy({ ...study, voice: e.target.value })}
            className={INPUT_CLASS}
          >
            {VOICES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Languages"
          hint="Primary is first; Lea is willing to switch into any extras the participant prefers. At least one is required."
        >
          <MultiLanguagePicker
            value={
              study.languages && study.languages.length > 0
                ? study.languages
                : [study.language || "en"]
            }
            onChange={(next) =>
              setStudy({
                ...study,
                language: next[0] ?? "en",
                languages: next,
              })
            }
          />
        </Field>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section label="Conversation">
        <Field
          label="First message"
          hint="Opening line Lea says when the call connects. Supports {{name}} placeholder."
        >
          <textarea
            value={study.firstMessage ?? ""}
            onChange={(e) =>
              setStudy({
                ...study,
                firstMessage: e.target.value === "" ? null : e.target.value,
              })
            }
            rows={2}
            className={`${INPUT_CLASS} resize-y`}
          />
        </Field>
      </Section>

      <hr className="my-10 border-neutral-200" />

      <Section
        label="Warmup"
        tag="Soon"
        description="Rapport questions Lea asks before the main script. Out of scope for v1."
      />

      <hr className="my-10 border-neutral-200" />

      <Section
        label="Sections"
        description="Structured outline of main interview questions, grouped into named sections. Lea announces transitions between sections and covers every question in a section before advancing. Drag the handle to reorder."
      >
        <SectionsBlock
          sections={study.sections ?? []}
          setSections={(next) => {
            const resolved =
              typeof next === "function"
                ? next(study.sections ?? [])
                : next;
            setStudy({
              ...study,
              sections: resolved.length === 0 ? null : resolved,
            });
          }}
        />
      </Section>
    </div>
  );
}

// ── Setup helpers ──────────────────────────────────────────────────────────

const INPUT_CLASS =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-[14px] text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-neutral-400";

function Section({
  label,
  description,
  tag,
  children,
}: {
  label: string;
  description?: string;
  tag?: string;
  children?: ReactNode;
}) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-x-10 gap-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-neutral-700">
            {label}
          </h3>
          {tag && (
            <span className="inline-flex items-center rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
              {tag}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 text-[12px] text-neutral-500 leading-relaxed">
            {description}
          </p>
        )}
      </div>
      <div className={`space-y-4 ${tag === "Soon" ? "opacity-50 pointer-events-none" : ""}`}>
        {children}
      </div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[13px] font-medium text-neutral-700 mb-1.5">
        {label}
      </span>
      {children}
      {hint && (
        <p className="mt-1 text-[12px] text-neutral-500 leading-relaxed">
          {hint}
        </p>
      )}
    </label>
  );
}

function StudyTypePicker({
  value,
  onChange,
}: {
  value: NonNullable<AdminStudy["studyType"]> | string;
  onChange: (v: NonNullable<AdminStudy["studyType"]>) => void;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <TypeCard
        title="Guided survey"
        body="Structured: Lea works through your section/question outline in order. Better when you have specific data to collect."
        selected={value === "guided"}
        onClick={() => onChange("guided")}
      />
      <TypeCard
        title="Discovery"
        body="Open-ended: Lea explores from the prompt, follows interesting threads. Better for early-stage research."
        selected={value === "discovery" || !value}
        onClick={() => onChange("discovery")}
      />
    </div>
  );
}

function TypeCard({
  title,
  body,
  selected,
  onClick,
}: {
  title: string;
  body: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border-2 px-4 py-3 transition-colors ${
        selected
          ? "border-neutral-900 bg-neutral-50"
          : "border-neutral-200 bg-white hover:border-neutral-300"
      }`}
    >
      <h4 className="text-[14px] font-semibold text-neutral-900 mb-1">{title}</h4>
      <p className="text-[12px] text-neutral-600 leading-relaxed">{body}</p>
    </button>
  );
}

function GatesFields({
  study,
  setStudy,
}: {
  study: AdminStudy;
  setStudy: (s: AdminStudy) => void;
}) {
  const gates = (study as AdminStudy & { gates?: Record<string, number> | null }).gates ?? {};
  const setGate = (key: string, value: string) => {
    const next = { ...gates };
    if (value === "") delete next[key];
    else next[key] = Number(value);
    setStudy({
      ...study,
      ...({ gates: Object.keys(next).length === 0 ? null : next } as Partial<AdminStudy>),
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Field label="Min participant turns" hint="How many times the participant must speak.">
        <input
          type="number"
          min={1}
          value={gates.minQuestionsAnswered ?? ""}
          onChange={(e) => setGate("minQuestionsAnswered", e.target.value)}
          className={INPUT_CLASS}
        />
      </Field>
      <Field label="Min participant words" hint="Total words across all participant turns.">
        <input
          type="number"
          min={1}
          value={gates.minParticipantWords ?? ""}
          onChange={(e) => setGate("minParticipantWords", e.target.value)}
          className={INPUT_CLASS}
        />
      </Field>
      <Field label="Min time (% of estimate)" hint="1–100. Duration must reach N% of the estimate above.">
        <input
          type="number"
          min={1}
          max={100}
          value={gates.minEstimatedTimePct ?? ""}
          onChange={(e) => setGate("minEstimatedTimePct", e.target.value)}
          className={INPUT_CLASS}
        />
      </Field>
    </div>
  );
}

// ── Analytics tab ──────────────────────────────────────────────────────────
// Cost rollup (§11.6) lands here. Time-series + status/sentiment pies are
// next-iteration polish; the cost numbers (computed at write time from
// Realtime usage × pricing) are the one bit of analytics that's actually
// implementable today, so we surface them in full instead of stubbing.

function AnalyticsTab({ study }: { study: AdminStudy }) {
  const [cost, setCost] = useState<StudyCost | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    studies
      .cost(study.id)
      .then(setCost)
      .catch((err) => setError(formatApiError(err)));
  }, [study.id]);

  return (
    <div className="space-y-8">
      {/* ── Cost rollup ──────────────────────────────────────────────── */}
      <section>
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          Cost — estimated
        </h2>
        {error && <ErrorBanner message={error} />}
        {cost === null && !error && (
          <p className="text-[13px] text-neutral-500">Loading…</p>
        )}
        {cost && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <CostCard
              label="Full campaign cost"
              value={`$${cost.grandTotalUsd.toFixed(2)}`}
              hint="OpenAI + rewards"
              accent="border-t-violet-300"
            />
            <CostCard
              label="OpenAI spend"
              value={`$${cost.totalUsd.toFixed(4)}`}
              hint="Realtime + insights tokens"
            />
            <CostCard
              label="Rewards"
              value={`$${cost.rewardsUsd.toFixed(2)}`}
              hint={
                cost.rewardUnitUsd != null
                  ? `${cost.validInterviewCount} valid × $${cost.rewardUnitUsd.toFixed(2)}`
                  : "No reward configured"
              }
            />
            <CostCard
              label="Interviews counted"
              value={String(cost.interviewCount)}
              hint={`${cost.validInterviewCount} valid`}
            />
            <CostCard
              label="Avg per valid"
              value={
                cost.avgPerValid != null && cost.validInterviewCount > 0
                  ? `$${(
                      cost.grandTotalUsd / cost.validInterviewCount
                    ).toFixed(2)}`
                  : "—"
              }
              hint="Full cost ÷ valid interviews"
            />
          </div>
        )}
        <p className="mt-2 text-[11px] text-neutral-500 leading-relaxed">
          Computed from per-interview Realtime + insights token usage × the
          pricing constants in{" "}
          <code className="font-mono">apps/api/.../pricing.ts</code>. Realtime
          cost is captured at PATCH finish-time; insights cost bumps the row
          atomically when{" "}
          <code className="font-mono">/interviews/:id/insights</code> runs.
          Estimated, not billed.
        </p>
      </section>

      {/* ── Charts placeholder — kept minimal until we have data shapes ── */}
      <section>
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          Trends
        </h2>
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-6 py-8 text-center">
          <p className="text-[13px] text-neutral-500 leading-relaxed max-w-md mx-auto">
            Completed-over-time line chart and status / sentiment pies will
            land here. For now, filter the{" "}
            <Link
              to={`/interviews`}
              className="font-medium text-neutral-700 hover:text-neutral-900 hover:underline"
            >
              Interviews database
            </Link>{" "}
            by this study for the row-level data.
          </p>
        </div>
      </section>
    </div>
  );
}

function CostCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <div
      className={`rounded-lg border border-neutral-200 ${
        accent ? `border-t-2 ${accent}` : ""
      } bg-white px-5 py-4`}
    >
      <div className="text-[24px] font-semibold text-neutral-900 tabular-nums leading-none mb-2">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
        {label}
      </div>
      {hint && (
        <div className="mt-1 text-[11px] text-neutral-500 leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}

// ── MultiLanguagePicker ───────────────────────────────────────────────────
// Searchable multi-select over LANGUAGES. Order matters: the first selected
// is the primary language (written to study.language; the rest to
// study.languages). Last language can't be cleared — the agent needs at
// least one. Adapted from `_prototype/.../studies/[id]/page.tsx`.

function MultiLanguagePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = value
    .map((code) => LANGUAGES.find((l) => l.code === code))
    .filter((l): l is (typeof LANGUAGES)[number] => Boolean(l));

  const filtered = LANGUAGES.filter((l) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return l.label.toLowerCase().includes(q) || l.code.toLowerCase().includes(q);
  });

  function toggle(code: string) {
    if (value.includes(code)) {
      // Don't allow clearing the last language — the agent needs at least
      // one to run. No-op silently rather than showing a disabled checkbox.
      if (value.length === 1) return;
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  }

  function makePrimary(code: string) {
    if (!value.includes(code) || value[0] === code) return;
    onChange([code, ...value.filter((c) => c !== code)]);
  }

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger — shows flag pills for selected + chevron */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 rounded-md border bg-white hover:bg-neutral-50 px-3 py-2 text-left text-[14px] transition-colors ${
          open
            ? "border-neutral-400"
            : "border-neutral-200 hover:border-neutral-300"
        }`}
      >
        <span className="flex items-center gap-1.5 flex-wrap min-w-0">
          {selected.length === 0 ? (
            <span className="text-neutral-400 italic">No language selected</span>
          ) : (
            selected.map((l, i) => (
              <span
                key={l.code}
                className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium border ${
                  i === 0
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-neutral-100 text-neutral-700 border-neutral-200"
                }`}
                title={i === 0 ? "Primary language" : "Additional language"}
              >
                <span>{l.flag}</span>
                <span>{l.label}</span>
                {i === 0 && (
                  <span className="text-[9px] uppercase tracking-wider opacity-70">
                    primary
                  </span>
                )}
              </span>
            ))
          )}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 16 16"
          aria-hidden
          className="text-neutral-400 shrink-0"
        >
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-[320px] flex flex-col rounded-lg border border-neutral-200 bg-white shadow-lg">
          <div className="px-2 py-1.5 border-b border-neutral-100">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Search languages…"
              className="w-full px-2 py-1 text-[13px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
            />
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[13px] text-neutral-400 italic text-center">
                No matches
              </div>
            ) : (
              filtered.map((l) => {
                const checked = value.includes(l.code);
                const isPrimary = value[0] === l.code;
                return (
                  <div
                    key={l.code}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-neutral-50"
                  >
                    <button
                      type="button"
                      onClick={() => toggle(l.code)}
                      className="flex-1 flex items-center gap-2 text-left min-w-0"
                    >
                      <span
                        className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border transition-colors ${
                          checked
                            ? "bg-neutral-900 border-neutral-900 text-white"
                            : "bg-white border-neutral-300"
                        }`}
                        aria-hidden
                      >
                        {checked && (
                          <svg width="9" height="9" viewBox="0 0 16 16">
                            <path
                              d="M3 8.5l3.5 3.5L13 5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span className="text-[16px] leading-none">{l.flag}</span>
                      <span className="text-[13px] text-neutral-900 truncate">
                        {l.label}
                      </span>
                      <span className="text-[10px] font-mono text-neutral-400 uppercase">
                        {l.code}
                      </span>
                    </button>
                    {checked && !isPrimary && (
                      <button
                        type="button"
                        onClick={() => makePrimary(l.code)}
                        className="text-[10px] uppercase tracking-wider text-neutral-400 hover:text-neutral-900 transition-colors shrink-0"
                        title="Make primary"
                      >
                        Make primary
                      </button>
                    )}
                    {checked && isPrimary && (
                      <span className="text-[10px] uppercase tracking-wider text-neutral-700 font-semibold shrink-0">
                        Primary
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── RewardPicker ───────────────────────────────────────────────────────────
// Searchable dropdown over existing rewards + "+ Create new reward" inline
// dialog. Selected reward shows as a chip with name + price. Cleared = null
// rewardId (no reward promised — valid for unpaid studies).

function RewardPicker({
  value,
  onChange,
}: {
  /** Currently-selected reward slug, or null = no reward. */
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const [rewards, setRewards] = useState<AdminReward[] | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Lazy-load the list — opening the picker is rare and rewards are tiny.
  useEffect(() => {
    if (!open || rewards !== null) return;
    rewardsApi
      .list()
      .then(({ rewards: rows }) => setRewards(rows))
      .catch(() => setRewards([]));
  }, [open, rewards]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = rewards?.find((r) => r.id === value) ?? null;

  const filtered = (rewards ?? []).filter((r) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q) ||
      (r.comment ?? "").toLowerCase().includes(q)
    );
  });

  function handleCreated(r: AdminReward) {
    setRewards((prev) => (prev ? [...prev, r] : [r]));
    onChange(r.id);
    setCreating(false);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 rounded-md border bg-white hover:bg-neutral-50 px-3 py-2 text-left transition-colors ${
          open
            ? "border-neutral-400"
            : "border-neutral-200 hover:border-neutral-300"
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected ? (
            <>
              <span className="text-[14px] font-medium text-neutral-900 truncate">
                {selected.name}
              </span>
              {selected.price != null && (
                <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 tabular-nums">
                  ${selected.price.toFixed(2)}
                </span>
              )}
              <code className="text-[10px] text-neutral-400 font-mono truncate">
                {selected.id}
              </code>
            </>
          ) : value ? (
            // Picker hasn't loaded yet but study has a rewardId set — show
            // the raw id with a "loading…" hint so the user knows we
            // didn't lose their selection.
            <span className="text-[13px] text-neutral-500">
              <code className="font-mono">{value}</code>
              <span className="ml-2 text-neutral-400 italic">resolving…</span>
            </span>
          ) : (
            <span className="text-[13px] text-neutral-400 italic">
              No reward
            </span>
          )}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 16 16"
          aria-hidden
          className="text-neutral-400 shrink-0"
        >
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-[340px] flex flex-col rounded-lg border border-neutral-200 bg-white shadow-lg">
          <div className="px-2 py-1.5 border-b border-neutral-100">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Search rewards…"
              className="w-full px-2 py-1 text-[13px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
            />
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {/* "No reward" option — explicit clear, in case the researcher
                deliberately wants an unpaid study. */}
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-neutral-50 ${
                value === null ? "bg-neutral-50" : ""
              }`}
            >
              <span className="text-[13px] text-neutral-700 italic">
                No reward
              </span>
              {value === null && (
                <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
                  <path
                    d="M3 8.5l3.5 3.5L13 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>

            {rewards === null && (
              <div className="px-3 py-4 text-[13px] text-neutral-500 italic text-center">
                Loading…
              </div>
            )}
            {rewards !== null && filtered.length === 0 && (
              <div className="px-3 py-4 text-[13px] text-neutral-400 italic text-center">
                {query ? "No matches" : "No rewards yet"}
              </div>
            )}
            {filtered.map((r) => {
              const checked = value === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    onChange(r.id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-start justify-between gap-2 px-3 py-2 text-left hover:bg-neutral-50 ${
                    checked ? "bg-neutral-50" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-medium text-neutral-900 truncate">
                        {r.name}
                      </span>
                      {r.price != null && (
                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 tabular-nums">
                          ${r.price.toFixed(2)}
                        </span>
                      )}
                      <code className="text-[10px] text-neutral-400 font-mono">
                        {r.id}
                      </code>
                    </div>
                    {r.comment && (
                      <p className="mt-0.5 text-[11px] text-neutral-500 truncate">
                        {r.comment}
                      </p>
                    )}
                  </div>
                  {checked && (
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 16 16"
                      aria-hidden
                      className="text-neutral-700 mt-1 shrink-0"
                    >
                      <path
                        d="M3 8.5l3.5 3.5L13 5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          <div className="border-t border-neutral-100 px-2 py-1.5">
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="w-full text-left px-2 py-1 text-[12px] text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded transition-colors inline-flex items-center gap-1.5"
            >
              <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
                <path
                  d="M8 3v10M3 8h10"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              Create new reward
            </button>
          </div>
        </div>
      )}

      {creating && (
        <CreateRewardDialog
          onCancel={() => setCreating(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

function CreateRewardDialog({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (r: AdminReward) => void;
}) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onCancel();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onCancel, saving]);

  async function submit() {
    if (!id.trim() || !name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { reward } = await rewardsApi.create({
        id: id.trim(),
        name: name.trim(),
        price: price.trim() === "" ? null : Number(price),
        comment: comment.trim() === "" ? null : comment.trim(),
      });
      onCreated(reward);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-3"
      >
        <h2 className="text-[16px] font-semibold text-neutral-900">
          New reward
        </h2>
        {error && <ErrorBanner message={error} />}

        <label className="block">
          <span className="block text-[12px] font-medium text-neutral-700 mb-1">
            Slug{" "}
            <span className="text-neutral-400 font-normal">
              (e.g. rwd-amazon-25)
            </span>
          </span>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            autoFocus
            className={`${INPUT_CLASS} font-mono text-[13px]`}
            placeholder="rwd-amazon-25"
          />
        </label>

        <label className="block">
          <span className="block text-[12px] font-medium text-neutral-700 mb-1">
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={INPUT_CLASS}
            placeholder="$25 Amazon gift card"
          />
        </label>

        <label className="block">
          <span className="block text-[12px] font-medium text-neutral-700 mb-1">
            Price (USD)
            <span className="text-neutral-400 font-normal ml-1">— optional</span>
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className={INPUT_CLASS}
            placeholder="25.00"
          />
        </label>

        <label className="block">
          <span className="block text-[12px] font-medium text-neutral-700 mb-1">
            Comment
            <span className="text-neutral-400 font-normal ml-1">— optional</span>
          </span>
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className={INPUT_CLASS}
            placeholder="Sent via Tango Card within 7 days"
          />
        </label>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="text-[13px] text-neutral-600 hover:text-neutral-900 px-3 py-2 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving || !id.trim() || !name.trim()}
            className="rounded-md bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-300 px-4 py-2 text-[13px] font-medium text-white transition-colors"
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sections drag-reorder UI ──────────────────────────────────────────────
// Ported from `_prototype/.../studies/[id]/page.tsx` SectionsBlock chain.
// Uses native HTML5 drag-and-drop (no library) — the handle is the
// `draggable` button, the card is the drop target. Index-based moves via
// `moveItem` keep the state pure.

function SectionsBlock({
  sections,
  setSections,
}: {
  sections: Section[];
  setSections: (next: Section[] | ((prev: Section[]) => Section[])) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragSection, setDragSection] = useState<number | null>(null);

  function addSection() {
    setSections((prev) => [...prev, makeSection()]);
  }
  function deleteSection(idx: number) {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  }
  function renameSection(idx: number, name: string) {
    setSections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, name } : s)),
    );
  }
  function updateQuestions(idx: number, questions: Question[]) {
    setSections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, questions } : s)),
    );
  }
  function toggleCollapsed(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      {sections.length === 0 && (
        <p className="text-[12px] text-neutral-400 italic">
          No sections yet — add one below to start outlining your interview.
        </p>
      )}

      {sections.map((sec, idx) => (
        <SectionCard
          key={sec.id}
          section={sec}
          index={idx}
          collapsed={collapsed.has(sec.id)}
          isDragging={dragSection === idx}
          onToggleCollapsed={() => toggleCollapsed(sec.id)}
          onRename={(name) => renameSection(idx, name)}
          onDelete={() => deleteSection(idx)}
          onQuestionsChange={(qs) => updateQuestions(idx, qs)}
          onDragStart={() => setDragSection(idx)}
          onDragEnd={() => setDragSection(null)}
          onDropOn={() => {
            if (dragSection !== null && dragSection !== idx) {
              setSections((prev) => moveItem(prev, dragSection, idx));
            }
            setDragSection(null);
          }}
        />
      ))}

      <button
        type="button"
        onClick={addSection}
        className="inline-flex items-center gap-1.5 mt-1 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 px-3 py-2 text-[12px] font-medium text-neutral-700 transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
          <path
            d="M8 3v10M3 8h10"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        Add section
      </button>
    </div>
  );
}

function SectionCard({
  section,
  index,
  collapsed,
  isDragging,
  onToggleCollapsed,
  onRename,
  onDelete,
  onQuestionsChange,
  onDragStart,
  onDragEnd,
  onDropOn,
}: {
  section: Section;
  index: number;
  collapsed: boolean;
  isDragging: boolean;
  onToggleCollapsed: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onQuestionsChange: (qs: Question[]) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropOn: () => void;
}) {
  const placeholder = `Section ${index + 1}`;
  const qCount = section.questions.length;
  const sectionMinutes = section.questions.reduce(
    (sum, q) => (q.text.trim() ? sum + DEPTH_MINUTES[q.depth] : sum),
    0,
  );

  return (
    <div
      className={`rounded-lg border bg-white transition-opacity ${
        isDragging
          ? "border-neutral-400 opacity-50"
          : "border-neutral-200"
      }`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDropOn();
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          aria-label={`Drag to reorder ${section.name || placeholder}`}
          className="cursor-grab active:cursor-grabbing p-1 text-neutral-400 hover:text-neutral-700 transition-colors"
        >
          <DragHandleIcon />
        </button>

        <input
          type="text"
          value={section.name}
          onChange={(e) => onRename(e.target.value)}
          placeholder={placeholder}
          aria-label="Section name"
          className="flex-1 min-w-0 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100 transition"
        />

        <div className="shrink-0 flex items-center gap-1.5">
          <span
            className="inline-flex items-center justify-center w-12 rounded-md border border-violet-200 bg-violet-50 text-violet-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider tabular-nums"
            aria-label={`${qCount} question${qCount === 1 ? "" : "s"}`}
          >
            {qCount} Q
          </span>
          <span
            className={`inline-flex items-center justify-center gap-1 w-20 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider tabular-nums ${
              sectionMinutes > 0
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-transparent text-transparent"
            }`}
            aria-hidden={sectionMinutes > 0 ? undefined : true}
          >
            <svg width="9" height="9" viewBox="0 0 16 16" aria-hidden>
              <circle cx="8" cy="8.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path d="M8 5.5V8.5l2 1.5M6 2h4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            ~{sectionMinutes > 0 ? formatSectionMinutes(sectionMinutes) : 0} min
          </span>
        </div>

        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand section" : "Collapse section"}
          className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            aria-hidden
            className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
          >
            <path d="M4 10l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete section"
          className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <TrashIcon />
        </button>
      </div>

      {!collapsed && (
        <div className="border-t border-neutral-100 px-3 py-3 space-y-3">
          <QuestionsList
            questions={section.questions}
            onChange={onQuestionsChange}
          />
        </div>
      )}
    </div>
  );
}

function QuestionsList({
  questions,
  onChange,
}: {
  questions: Question[];
  onChange: (qs: Question[]) => void;
}) {
  const [dragQuestion, setDragQuestion] = useState<number | null>(null);

  function addQuestion() {
    onChange([...questions, makeQuestion()]);
  }
  function deleteAt(i: number) {
    onChange(questions.filter((_, idx) => idx !== i));
  }
  function patchAt(i: number, patch: Partial<Question>) {
    onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  }

  return (
    <div className="space-y-2">
      {questions.length === 0 && (
        <p className="text-[12px] text-neutral-400 italic px-2 py-3">
          No questions yet.
        </p>
      )}
      {questions.map((q, i) => (
        <QuestionRow
          key={q.id}
          index={i}
          question={q}
          isDragging={dragQuestion === i}
          onDragStart={() => setDragQuestion(i)}
          onDragEnd={() => setDragQuestion(null)}
          onDropOn={() => {
            if (dragQuestion !== null && dragQuestion !== i) {
              onChange(moveItem(questions, dragQuestion, i));
            }
            setDragQuestion(null);
          }}
          onTextChange={(text) => patchAt(i, { text })}
          onDepthChange={(depth) => patchAt(i, { depth })}
          onDelete={() => deleteAt(i)}
        />
      ))}

      <button
        type="button"
        onClick={addQuestion}
        className="inline-flex items-center gap-1.5 mt-1 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 px-3 py-1.5 text-[12px] font-medium text-neutral-700 transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        Add question
      </button>
    </div>
  );
}

function QuestionRow({
  index,
  question,
  isDragging,
  onDragStart,
  onDragEnd,
  onDropOn,
  onTextChange,
  onDepthChange,
  onDelete,
}: {
  index: number;
  question: Question;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropOn: () => void;
  onTextChange: (text: string) => void;
  onDepthChange: (depth: QuestionDepth) => void;
  onDelete: () => void;
}) {
  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDropOn();
      }}
      className={`rounded-md border bg-neutral-50/40 p-3 transition-opacity ${
        isDragging
          ? "border-neutral-400 opacity-50"
          : "border-neutral-200"
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        <button
          type="button"
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          aria-label={`Drag question ${index + 1} to reorder`}
          className="mt-0.5 cursor-grab active:cursor-grabbing p-1 text-neutral-400 hover:text-neutral-700 transition-colors"
        >
          <DragHandleIcon />
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">
            Question {index + 1}
          </div>
          <textarea
            value={question.text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="What do you want to ask?"
            rows={2}
            className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-[13px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100 transition resize-y"
          />
        </div>

        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete question"
          className="mt-5 p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
        >
          <TrashIcon />
        </button>
      </div>

      <div className="flex items-center gap-2 ml-7 mt-1">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
          Depth
        </span>
        <DepthSelector value={question.depth} onChange={onDepthChange} />
        <span className="text-[10px] text-neutral-400 tabular-nums">
          ~{DEPTH_MINUTES[question.depth]} min
        </span>
      </div>
    </div>
  );
}

function DepthSelector({
  value,
  onChange,
}: {
  value: QuestionDepth;
  onChange: (next: QuestionDepth) => void;
}) {
  const options: { value: QuestionDepth; label: string; hint: string }[] = [
    { value: "light", label: "Light", hint: "~1.5 min · 1 probe" },
    { value: "medium", label: "Medium", hint: "~3 min · 2 probes" },
    { value: "deep", label: "Deep", hint: "~5 min · 3+ probes" },
  ];
  return (
    <div
      role="radiogroup"
      className="inline-flex rounded-md border border-neutral-200 bg-white p-0.5 text-[12px] font-medium"
    >
      {options.map((o) => {
        const sel = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={sel}
            onClick={() => onChange(o.value)}
            title={o.hint}
            className={`px-2.5 py-1 rounded transition-colors ${
              sel
                ? "bg-neutral-900 text-white"
                : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Section helpers (ported from prototype) ───────────────────────────────

/** Short random id — prefixed `s_` / `q_` so we don't accidentally collide
 *  with a study id elsewhere. Same shape the prototype uses. */
function uid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function makeQuestion(): Question {
  return { id: uid("q"), text: "", depth: "medium" };
}
function makeSection(): Section {
  return { id: uid("s"), name: "", questions: [makeQuestion()] };
}

/** Pure: returns a new array with the element at `from` moved to `to`. */
function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0) return arr;
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return arr;
  next.splice(to, 0, item);
  return next;
}

function formatSectionMinutes(mins: number): string {
  if (Number.isInteger(mins)) return String(mins);
  const oneDp = Math.round(mins * 10) / 10;
  return String(oneDp);
}

function DragHandleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
      <circle cx="6" cy="4" r="1" fill="currentColor" />
      <circle cx="10" cy="4" r="1" fill="currentColor" />
      <circle cx="6" cy="8" r="1" fill="currentColor" />
      <circle cx="10" cy="8" r="1" fill="currentColor" />
      <circle cx="6" cy="12" r="1" fill="currentColor" />
      <circle cx="10" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M3 4h10M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M6 7v5M10 7v5M4 4l1 10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l1-10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
